import dayjs from 'dayjs';
import {useSelector} from 'react-redux';
import {useEffect, useState} from 'react';
import {
  Button,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  ThemeProvider,
  Typography,
} from '@mui/material';
import {
  companyIdSelector,
  isGTSelector,
} from '../../../store/Slices/companySlice';
import {
  IGetStingerMessageRequestResponse,
  useRqViewStingerListMessagesByRequestID,
} from '../../../react_query/messages/message-requests/StingerMessageList';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import {RequestTheme} from '../../../components/layouts/Theme';
import {useStoreSelector} from '../../../store/configstore';
import {UserCanAny} from '../../../components/UserCan';
import {useUserCanAny} from '../../../hooks/useUserCan';

export const ViewStingerMsgShowCmpt = ({
  id,
  handleOpen,
}: {
  id: number;
  handleOpen: () => void;
}) => {
  const companyId = useStoreSelector(companyIdSelector);
  const isGT = useSelector(isGTSelector);
  const isAdminAndViewStaff = useUserCanAny('is-admin,stinger-message:show');

  const [content, setContent] = useState<IGetStingerMessageRequestResponse>();

  const {data, isFetched, isError} = useRqViewStingerListMessagesByRequestID(
    companyId,
    id,
    Boolean(id),
  );
  const [hasExpireDate, setHasExpireDate] = useState<boolean>(false);
  useEffect(() => {
    if (isFetched && !isError && data) {
      setContent(data);
      setHasExpireDate(Boolean(data?.expire_date));
    }
  }, [data, isError, isFetched]);

  return (
    <ThemeProvider theme={RequestTheme}>
      <Container className='p-0'>
        <TableContainer className='bg-transparent'>
          <Table>
            <TableBody>
              <>
                {isGT && (
                  <TableRow>
                    <TableCell width={200}>School(s)</TableCell>
                    <TableCell>
                      {content?.schools?.map(val => val.name).join(', ')}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell width={200}>Station(s)</TableCell>
                  <TableCell>
                    {content?.stations
                      ?.map(val => val.label || val.name)
                      .join(', ')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={200}>Message Name</TableCell>
                  <TableCell>{content?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={200}>Content</TableCell>
                  <TableCell>
                    <pre>{content?.message?.message}</pre>
                  </TableCell>
                </TableRow>
                {content?.events && content?.events?.length ? (
                  <TableRow>
                    <TableCell width={200}>Sports / Events</TableCell>
                    <TableCell>
                      {content?.events
                        .map(event => event.event_name)
                        .join(', ')}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell width={200}>Season(s)</TableCell>
                    <TableCell>
                      {content?.is_all_seasons === 1
                        ? 'Year Around'
                        : content?.seasons
                            ?.map(season => season.season_name)
                            .join(', ')}
                    </TableCell>
                  </TableRow>
                )}
                {isAdminAndViewStaff && (
                  <>
                    <TableRow>
                      <TableCell width={200}>Voice Artist</TableCell>
                      <TableCell>
                        {content?.voice_artist_name || 'NA'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width={200}>Message Creator</TableCell>
                      <TableCell>
                        {content?.message_creator_name || 'NA'}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell width={200}>Created Date</TableCell>
                  <TableCell>
                    {dayjs(content?.created_at).format('MM/DD/YYYY hh:mm:ss A')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width={200}>Created By</TableCell>
                  <TableCell>{content?.created_by?.name}</TableCell>
                </TableRow>
                {hasExpireDate ? (
                  <TableRow>
                    <TableCell>Expire Date</TableCell>
                    <TableCell>{content?.expire_date}</TableCell>
                  </TableRow>
                ) : null}
                {content?.message?.media_track ? (
                  <TableRow>
                    <TableCell width={200}>File</TableCell>
                    <TableCell>
                      {content?.message.media_track ? (
                        <Stack spacing={1} direction='row' alignItems='center'>
                          <MusicPreviewBtn
                            id={content?.message.media_track.id}
                            url={content?.message.media_track.url}
                            track_name={
                              content?.message?.media_track?.track_name ||
                              content?.name
                            }
                            track_artist={
                              content?.message?.media_track?.track_artist ||
                              content?.message?.message
                            }
                            isDownloadable
                            downloadFileName={
                              content?.message.media_track.track_name
                            }
                          />
                          <Typography>
                            {content?.message.media_track?.track_name}
                          </Typography>
                        </Stack>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : null}
              </>
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction='row' spacing={2} mt={2} justifyContent='center'>
          {data?.status !== 'In Progress' ? (
            <UserCanAny scope='is-admin,stinger-message:update-status'>
              {data?.status === 'Inactive' ? (
                <Button
                  variant='outlined'
                  color='success'
                  size='small'
                  onClick={handleOpen}>
                  Restore
                </Button>
              ) : (
                <Button
                  variant='outlined'
                  color='error'
                  size='small'
                  onClick={handleOpen}>
                  Deactivate
                </Button>
              )}
            </UserCanAny>
          ) : null}
        </Stack>
      </Container>
    </ThemeProvider>
  );
};
